import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { Link } from "react-router-dom";

export default function Accessibility() {
  return (
    <>
      <SEOHead
        title="Accessibility Statement"
        description="Temple Israel of Brevard is committed to digital accessibility for everyone. Read our full WCAG 2.1 AA accessibility statement."
        path="/accessibility"
      />
      <PageHero
        eyebrow="Accessibility"
        title="Website Accessibility Statement"
        subtitle="Last updated: February 27, 2026"
      />
      <section className="bg-[var(--paper)] py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 prose-tiob" data-testid="accessibility-statement">
          <h2>Our Commitment to Accessibility</h2>
          <p>
            We are committed to ensuring digital accessibility for people with disabilities.
            We are continually improving the user experience for everyone and applying relevant
            accessibility standards to enhance usability and inclusivity. Our goal is to provide
            a website that is accessible to the widest possible audience, regardless of technology
            or ability.
          </p>

          <h2>Accessibility Standards</h2>
          <p>
            We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA,
            which are widely recognized as the industry standard for website accessibility.
            Accessibility is an ongoing process, and we are actively working toward improving the
            accessibility and usability of our website.
          </p>

          <h2>Accessibility Technology &amp; Tools</h2>
          <p>
            To support our accessibility efforts, we have deployed industry-standard accessibility
            technology designed to enhance usability and assist visitors with disabilities. These
            tools may provide features such as:
          </p>
          <ul>
            <li>Screen reader enhancements</li>
            <li>Keyboard navigation improvements</li>
            <li>Color contrast adjustments</li>
            <li>Text size and spacing controls</li>
            <li>Focus indicators</li>
            <li>Additional accessibility interface options</li>
          </ul>
          <p>
            This technology helps us improve accessibility while we continue broader auditing,
            remediation, and development efforts.
          </p>

          <h2>Ongoing Improvement</h2>
          <p>Accessibility is an ongoing initiative. We are:</p>
          <ul>
            <li>Reviewing site content and functionality regularly</li>
            <li>Working with developers and accessibility tools</li>
            <li>Identifying and remediating barriers</li>
            <li>Incorporating accessibility into future design and development</li>
          </ul>
          <p>
            We understand accessibility is a continuous journey and are committed to making
            improvements over time.
          </p>

          <h2>Third-Party Content</h2>
          <p>
            Some areas of the website may include third-party content, plugins, or integrations
            that are not fully controlled by us. While we encourage vendors to provide accessible
            content, we cannot guarantee accessibility of third-party features. However, we welcome
            feedback regarding any issues encountered.
          </p>

          <h2>Feedback &amp; Assistance</h2>
          <p>
            If you experience difficulty accessing any part of this website, need assistance, or
            would like to request content in an alternative format, please contact us. Please include:
          </p>
          <ul>
            <li>The page URL</li>
            <li>Description of the issue</li>
            <li>Your preferred contact method</li>
          </ul>
          <p>
            <strong>Contact:</strong> Information on our{" "}
            <Link to="/contact" className="text-[var(--cyan-deep)] underline">Contact Us</Link> page.
          </p>
          <p>
            We aim to respond within a reasonable timeframe and will make reasonable efforts to
            provide accessible alternatives.
          </p>

          <h2>Good Faith Efforts</h2>
          <p>
            We are making good-faith efforts to comply with accessibility standards and improve
            usability for individuals with disabilities. Despite our efforts, some content may not
            yet fully meet the strictest accessibility standards due to the evolving nature of
            technology and ongoing updates. We appreciate your understanding as we continue
            improving accessibility.
          </p>

          <h2>Terms &amp; Conditions - Accessibility Clause</h2>
          <p>
            We are committed to providing an accessible website experience for all users and
            strive to follow recognized accessibility standards, including WCAG guidelines.
            We have implemented industry-standard accessibility tools and continue to review,
            update, and improve our website to enhance accessibility. Because accessibility is
            an ongoing process, some areas of the website may not yet fully conform to all
            accessibility standards. If you encounter accessibility barriers, please contact us
            so we can provide assistance, offer alternative access, and address the issue where
            feasible. Nothing in these Terms is intended to limit any rights available under
            applicable accessibility laws.
          </p>
        </div>
      </section>
    </>
  );
}
